import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { setSessionContext } from '@tupai/analytics/client';
import { clearSignupDate, readSessionContext, setSignupDate } from '@/lib/analytics';
import { revokePushRegistration } from '@/lib/push-registration';
import { firebaseAuth } from '@/lib/firebase';

interface AuthState {
  token: string | null;
  // Stable parent identity from verify-otp — handed to RevenueCat
  // (identifyBillingUser) so purchases attach to this parent, not an
  // anonymous app_user_id. Null for sessions from before it was returned;
  // refreshed on next login.
  parentId: string | null;
  storedName: string | null;
  phone: string;
  // Email of an email-login parent. Kept here rather than read off Firebase's
  // currentUser: Firebase revokes the session once an email change is
  // verified, while this app's own token stays valid, so currentUser goes
  // null and the email would vanish from the details sheet.
  email: string;
  parentName: string;
  sessionExpired: boolean;
  /** Set by logout(): the signed-out redirect goes to /login, not /start (TUP-1757). Not persisted. */
  signedOut: boolean;
  expireSession: () => void;
  login: (token: string, parentId: string, name: string, phone: string, createdAt?: string) => void;
  logout: () => void;
  setToken: (v: string | null) => void;
  setStoredName: (v: string | null) => void;
  setPhone: (v: string) => void;
  setEmail: (v: string) => void;
  setParentName: (v: string) => void;
}

const STORAGE_KEY = 'tupai-auth';
const LEGACY_KEYS = ['parent_token', 'parent_name', 'parent_phone', 'parent_language'] as const;

// One-time migration: copy legacy individual keys into the persist blob, then clean up.
function migrateLegacyStorage() {
  if (localStorage.getItem(STORAGE_KEY)) return;
  const token = localStorage.getItem('parent_token');
  if (!token) return;
  const blob = {
    state: {
      token,
      storedName: localStorage.getItem('parent_name'),
      parentName: localStorage.getItem('parent_name') ?? '',
      phone: localStorage.getItem('parent_phone') ?? '',
    },
    version: 0,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
  LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
}

migrateLegacyStorage();

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      parentId: null,
      storedName: null,
      phone: '',
      email: '',
      parentName: '',
      sessionExpired: false,
      signedOut: false,
      expireSession: () => {
        revokePushRegistration();
        void firebaseAuth.signOut().catch(() => {});
        clearSignupDate();
        set({
          token: null,
          parentId: null,
          storedName: null,
          phone: '',
          email: '',
          parentName: '',
          sessionExpired: true,
        });
      },

      login: (token, parentId, name, phone, createdAt) => {
        // Server value is authoritative — stamped unconditionally on every
        // login (Phase 2.7, B1). setSignupDate no-ops on a missing/invalid
        // value, so an older server (no created_at yet) is a silent no-op.
        // Refreshed immediately (mirrors student-client's onAuthenticated)
        // rather than waiting for the next cold-start/resume to pick it up.
        setSignupDate(createdAt);
        setSessionContext(readSessionContext());
        set({
          token,
          parentId,
          storedName: name,
          parentName: name,
          phone,
          sessionExpired: false,
          signedOut: false,
        });
      },

      logout: () => {
        revokePushRegistration();
        void firebaseAuth.signOut().catch(() => {});
        // Shared-device hygiene, same as the token itself.
        clearSignupDate();
        set({
          token: null,
          parentId: null,
          storedName: null,
          phone: '',
          email: '',
          parentName: '',
          sessionExpired: false,
          signedOut: true,
        });
      },

      setToken: (v) => set({ token: v }),
      setStoredName: (v) => set({ storedName: v }),
      setPhone: (v) => set({ phone: v }),
      setEmail: (v) => set({ email: v }),
      setParentName: (v) => set({ parentName: v }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        parentId: state.parentId,
        storedName: state.storedName,
        phone: state.phone,
        email: state.email,
        parentName: state.parentName,
      }),
    }
  )
);
